import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, UserPlus, Building2 } from "lucide-react";
import { api } from "../lib/api";

// Página unificada para candidaturas de Consultor e Unidade Parceira (mode = "consultor" | "clinica").
const Apply = ({ mode = "consultor" }) => {
  const isClinic = mode === "clinica";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "",
    // clinic-only
    cnpj: "", city: "", state: "", address: "", beds: 0, website: "",
    modality: [], substances: [], audience: [],
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggle = (list, val) => set(list, form[list].includes(val)
    ? form[list].filter((x) => x !== val)
    : [...form[list], val]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        type: isClinic ? "clinica" : "consultor",
        name: form.name, email: form.email, phone: form.phone,
        message: form.message,
      };
      if (isClinic) {
        payload.clinic_data = {
          cnpj: form.cnpj, city: form.city, state: form.state,
          address: form.address, beds: Number(form.beds || 0),
          website: form.website,
          modality: form.modality, substances: form.substances,
          audience: form.audience,
        };
      }
      await api.post("/applications", payload);
      setDone(true);
      toast.success("Candidatura enviada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="apply-done">
        <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
        <h1 className="font-display font-bold text-3xl text-[#0B2B62] tracking-tight">Candidatura enviada</h1>
        <p className="text-slate-600 mt-3 max-w-md mx-auto">
          Recebemos sua candidatura. Nossa equipe de governança RAF vai analisar seus dados
          e entrar em contato em breve. Fique de olho no seu e-mail e no seu WhatsApp.
        </p>
        <Link to="/" className="inline-block mt-6 text-[#0B2B62] font-semibold hover:underline">Voltar ao início</Link>
      </div>
    );
  }

  const title = isClinic ? "Seja uma Unidade Parceira RAF" : "Quero ser um Consultor RAF";
  const subtitle = isClinic
    ? "Envie os dados da sua clínica. Após análise, credenciamento e alinhamento, publicamos seu perfil como Unidade Parceira RAF."
    : "Envie seus dados. Após análise, entrevistamos, alinhamos o método RAF e você recebe treinamento antes de assumir casos.";
  const Icon = isClinic ? Building2 : UserPlus;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid={`apply-${mode}`}>
      <Link to="/" className="text-sm text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Voltar
      </Link>
      <div className="mb-8 flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-[#0B2B62] text-white flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6" strokeWidth={1.6} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Candidatura</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">{title}</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">{subtitle}</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <Field label={isClinic ? "Nome da clínica" : "Seu nome completo"} required>
          <input required data-testid="apply-name" value={form.name} onChange={(e) => set("name", e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="E-mail de contato" required>
            <input required type="email" data-testid="apply-email" value={form.email} onChange={(e) => set("email", e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
          </Field>
          <Field label="Telefone / WhatsApp" required>
            <input required data-testid="apply-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
              placeholder="(11) 99999-9999"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
          </Field>
        </div>

        {isClinic && (
          <div className="p-5 border border-slate-200 rounded-xl bg-slate-50/40 space-y-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">Dados da Unidade</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="CNPJ"><input data-testid="apply-cnpj" value={form.cnpj} onChange={(e) => set("cnpj", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="Site (opcional)"><input data-testid="apply-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="Cidade" required><input required data-testid="apply-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="UF" required><input required maxLength={2} data-testid="apply-state" value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="Endereço"><input data-testid="apply-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="Vagas disponíveis"><input type="number" min={0} data-testid="apply-beds" value={form.beds} onChange={(e) => set("beds", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            </div>
            <MultiSelect label="Modalidades atendidas" options={["voluntária", "involuntária", "compulsória"]} selected={form.modality} onToggle={(v) => toggle("modality", v)} testidPrefix="apply-mod" />
            <MultiSelect label="Substâncias" options={["álcool", "cocaína", "crack", "maconha", "medicamentos", "outras"]} selected={form.substances} onToggle={(v) => toggle("substances", v)} testidPrefix="apply-sub" />
            <MultiSelect label="Público" options={["adulto", "adolescente", "idoso", "masculino", "feminino", "misto"]} selected={form.audience} onToggle={(v) => toggle("audience", v)} testidPrefix="apply-aud" />
          </div>
        )}

        <Field label={isClinic ? "Sobre a Unidade — diferenciais, proposta terapêutica" : "Sua experiência e motivação para atuar no RAF"} required>
          <textarea required rows={5} data-testid="apply-message" value={form.message} onChange={(e) => set("message", e.target.value)}
            placeholder={isClinic ? "Conte um pouco sobre a estrutura, equipe e método..." : "Conte sobre sua formação, experiência com dependência química e o que te motiva..."}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
        </Field>

        <div className="pt-2">
          <button type="submit" disabled={busy} data-testid="apply-submit"
            className="px-6 py-3 bg-[#0B2B62] text-white rounded-md font-semibold hover:bg-slate-800 disabled:opacity-50">
            {busy ? "Enviando..." : "Enviar candidatura"}
          </button>
          <p className="text-xs text-slate-500 mt-3">Ao enviar, você concorda que a RAF entre em contato para avaliar sua candidatura. Nenhuma conta é criada neste momento — nossa equipe de governança fará o cadastro após aprovação.</p>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const MultiSelect = ({ label, options, selected, onToggle, testidPrefix }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" data-testid={`${testidPrefix}-${o}`} onClick={() => onToggle(o)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selected.includes(o) ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
          {o}
        </button>
      ))}
    </div>
  </div>
);

export const ApplyConsultor = () => <Apply mode="consultor" />;
export const ApplyClinic = () => <Apply mode="clinica" />;
export default Apply;
